import React, { useState, useEffect } from 'react';
import { MessageSquare, Search, Plus, Clock, User, Trophy, ArrowLeft, Heart, Send, Eye, Pin, BookOpen, ExternalLink, Mail, UserPlus, Lock } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import CommunityLeaderboard from '../components/CommunityLeaderboard';
import HelpButton from '../components/HelpButton';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const CommunityPage = () => {
  const { user, isAuthenticated, demoMode } = useAuth();
  const canContribute = isAuthenticated && !demoMode; // Only real users can post
  
  const [activeTab, setActiveTab] = useState('discussions');
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  
  // Discussion detail states
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  
  // New discussion states
  const [showNewDiscussion, setShowNewDiscussion] = useState(false);
  const [newDiscussionData, setNewDiscussionData] = useState({
    title: '',
    content: '',
    category: ''
  });
  const [submittingDiscussion, setSubmittingDiscussion] = useState(false);
  const [categoryResources, setCategoryResources] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Format user name for privacy (First name + Last initial)
  const getDisplayName = () => {
    if (!user?.name) return 'Anonymous';
    const nameParts = user.name.trim().split(' ');
    if (nameParts.length === 1) return nameParts[0];
    const firstName = nameParts[0];
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastInitial}.`;
  };

  useEffect(() => {
    loadDiscussions();
    loadCategories();
  }, [selectedCategory]);

  const loadDiscussions = async () => {
    setLoading(true);
    try {
      const params = selectedCategory ? { category: selectedCategory } : {};
      const response = await axios.get(`${API_URL}/api/community/discussions`, { params });
      setDiscussions(response.data);
    } catch (error) {
      console.error('Error loading discussions:', error);
      // Fallback sample data
      setDiscussions([
        {
          id: 'disc-1',
          title: 'Tips for first IEP meeting?',
          content: 'My son was just diagnosed with autism and we have our first IEP meeting next week...',
          category: 'IEP & Education',
          author_name: 'Sarah M.',
          created_at: '2026-01-02T10:30:00',
          reply_count: 12,
          view_count: 156,
          is_pinned: true
        },
        {
          id: 'disc-2',
          title: 'Best sensory-friendly activities in Twin Cities?',
          content: 'Looking for sensory-friendly activities for my 6-year-old daughter...',
          category: 'Local Resources',
          author_name: 'David R.',
          created_at: '2026-01-01T16:45:00',
          reply_count: 8,
          view_count: 89,
          is_pinned: false
        }
      ]);
    }
    setLoading(false);
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/community/categories`);
      setCategories(response.data);
    } catch (error) {
      setCategories([
        'IEP & Education',
        'Therapies & Treatments',
        'Local Resources',
        'Support & Encouragement'
      ]);
    }
  };

  const openDiscussion = async (discussionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/community/discussions/${discussionId}`);
      setSelectedDiscussion(response.data);
      
      // Load resources for this category
      if (response.data.category) {
        try {
          const resourcesResponse = await axios.get(`${API_URL}/api/community/resources/${encodeURIComponent(response.data.category)}`);
          setCategoryResources(resourcesResponse.data.resources || []);
        } catch (e) {
          setCategoryResources([]);
        }
      }
    } catch (error) {
      console.error('Error loading discussion:', error);
      // Try to find in current discussions
      const disc = discussions.find(d => d.id === discussionId);
      if (disc) {
        setSelectedDiscussion({
          ...disc,
          replies: [
            { id: 'r1', content: 'Great question! I recommend bringing a notebook...', author_name: 'Jennifer K.', created_at: '2026-01-02T11:15:00', likes: 8 },
            { id: 'r2', content: 'Read up on your rights as a parent. PACER Center has great resources!', author_name: 'Mike T.', created_at: '2026-01-02T12:00:00', likes: 15 }
          ]
        });
      }
    }
  };

  const handleSubmitReply = async () => {
    if (!replyContent.trim() || !selectedDiscussion) return;
    
    setSubmittingReply(true);
    const displayName = getDisplayName();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/community/discussions/${selectedDiscussion.id}/replies`,
        { 
          content: replyContent,
          author_name: displayName
        }
      );
      
      // Add reply to current view
      const newReply = response.data.reply || {
        id: `r-${Date.now()}`,
        content: replyContent,
        author_name: displayName,
        created_at: new Date().toISOString(),
        likes: 0
      };
      
      setSelectedDiscussion({
        ...selectedDiscussion,
        replies: [...(selectedDiscussion.replies || []), newReply],
        reply_count: (selectedDiscussion.reply_count || 0) + 1
      });
      
      setReplyContent('');
    } catch (error) {
      console.error('Error submitting reply:', error);
      alert('Failed to submit reply. Please try again.');
    }
    setSubmittingReply(false);
  };

  const handleCreateDiscussion = async () => {
    if (!newDiscussionData.title.trim() || !newDiscussionData.content.trim() || !newDiscussionData.category) {
      alert('Please fill in all fields');
      return;
    }
    
    setSubmittingDiscussion(true);
    const displayName = getDisplayName();
    
    try {
      await axios.post(`${API_URL}/api/community/discussions`, {
        ...newDiscussionData,
        author_name: displayName
      });
      
      setShowNewDiscussion(false);
      setNewDiscussionData({ title: '', content: '', category: '' });
      loadDiscussions();
      alert('Discussion created successfully!');
    } catch (error) {
      console.error('Error creating discussion:', error);
      alert('Failed to create discussion. Please try again.');
    }
    setSubmittingDiscussion(false);
  };

  const handleLikeReply = async (replyId) => {
    if (!selectedDiscussion) return;
    
    try {
      await axios.post(`${API_URL}/api/community/discussions/${selectedDiscussion.id}/replies/${replyId}/like`);
      
      // Update local state
      setSelectedDiscussion({
        ...selectedDiscussion,
        replies: selectedDiscussion.replies.map(r => 
          r.id === replyId ? { ...r, likes: (r.likes || 0) + 1 } : r
        )
      });
    } catch (error) {
      console.error('Error liking reply:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const filteredDiscussions = discussions.filter(d => 
    d.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Discussion Detail View
  if (selectedDiscussion) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back button */}
          <button
            onClick={() => setSelectedDiscussion(null)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Discussions
          </button>

          {/* Discussion */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                {selectedDiscussion.is_pinned && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full mb-2">
                    <Pin className="w-3 h-3" /> Pinned
                  </span>
                )}
                <h1 className="text-2xl font-bold text-gray-900">{selectedDiscussion.title}</h1>
              </div>
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-full">
                {selectedDiscussion.category}
              </span>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {selectedDiscussion.author_name}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatDate(selectedDiscussion.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {selectedDiscussion.view_count || 0} views
              </span>
            </div>
            
            <p className="text-gray-700 whitespace-pre-wrap">{selectedDiscussion.content}</p>
          </div>

          {/* Replies */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedDiscussion.replies?.length || 0} Replies
            </h2>
            
            <div className="space-y-4 mb-6">
              {selectedDiscussion.replies?.map((reply) => (
                <div key={reply.id} className="border-b border-gray-100 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="font-medium text-gray-900">{reply.author_name}</span>
                      <span className="text-sm text-gray-500">{formatDate(reply.created_at)}</span>
                    </div>
                    <button
                      onClick={() => handleLikeReply(reply.id)}
                      className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 transition-colors"
                    >
                      <Heart className="w-4 h-4" />
                      {reply.likes || 0}
                    </button>
                  </div>
                  <p className="text-gray-700 ml-10">{reply.content}</p>
                </div>
              ))}
              
              {(!selectedDiscussion.replies || selectedDiscussion.replies.length === 0) && (
                <p className="text-gray-500 text-center py-4">No replies yet. Be the first to respond!</p>
              )}
            </div>

            {/* Reply Form */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Add Your Reply</h3>
              {canContribute ? (
                <>
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Share your thoughts, advice, or experience..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={handleSubmitReply}
                      disabled={!replyContent.trim() || submittingReply}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                    >
                      <Send className="w-4 h-4" />
                      {submittingReply ? 'Posting...' : 'Post Reply'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center text-gray-600">
                  <Lock size={18} className="mr-2" />
                  <span className="text-sm">
                    <strong>Create a free account</strong> to join the conversation and share your experiences.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Related Resources */}
          {categoryResources.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Helpful Resources for {selectedDiscussion.category}
                </h2>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryResources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 bg-purple-50 border border-purple-100 rounded-lg hover:bg-purple-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-purple-900">{resource.title}</h3>
                        <p className="text-sm text-purple-600 mt-1">{resource.source}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-purple-400 flex-shrink-0" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Community</h1>
            <p className="text-gray-600">Connect with other families, share experiences, and find support</p>
          </div>
          {canContribute ? (
            <button
              onClick={() => setShowNewDiscussion(true)}
              className="mt-4 md:mt-0 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors min-h-[44px]"
            >
              <Plus className="w-5 h-5" />
              Start Discussion
            </button>
          ) : (
            <button
              disabled
              className="mt-4 md:mt-0 flex items-center gap-2 bg-gray-400 text-white px-4 py-2 rounded-md cursor-not-allowed min-h-[44px]"
              title="Create an account to start discussions"
            >
              <Lock className="w-5 h-5" />
              Start Discussion
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-gray-200">
          {[
            { id: 'discussions', label: 'Discussions', icon: MessageSquare },
            { id: 'contribute', label: 'Contribute', icon: Trophy }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'discussions' && (
          <div>
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search discussions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Discussions List */}
            {loading ? (
              <LoadingSpinner text="Loading discussions..." />
            ) : (
              <div className="space-y-4">
                {filteredDiscussions.map((discussion) => (
                  <div
                    key={discussion.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:border-blue-300 transition-colors cursor-pointer"
                    onClick={() => openDiscussion(discussion.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {discussion.is_pinned && (
                            <Pin className="w-4 h-4 text-amber-500" />
                          )}
                          <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600">
                            {discussion.title}
                          </h3>
                        </div>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-2">
                          {discussion.content}
                        </p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {discussion.author_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDate(discussion.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            {discussion.reply_count || 0} replies
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {discussion.view_count || 0} views
                          </span>
                        </div>
                      </div>
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ml-4">
                        {discussion.category}
                      </span>
                    </div>
                  </div>
                ))}
                
                {filteredDiscussions.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No discussions found. Be the first to start one!
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'contribute' && (
          <CommunityLeaderboard />
        )}

        {/* New Discussion Modal */}
        {showNewDiscussion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Start a New Discussion</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={newDiscussionData.title}
                    onChange={(e) => setNewDiscussionData({ ...newDiscussionData, title: e.target.value })}
                    placeholder="What would you like to discuss?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newDiscussionData.category}
                    onChange={(e) => setNewDiscussionData({ ...newDiscussionData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    value={newDiscussionData.content}
                    onChange={(e) => setNewDiscussionData({ ...newDiscussionData, content: e.target.value })}
                    placeholder="Share your question, story, or topic..."
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowNewDiscussion(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDiscussion}
                  disabled={submittingDiscussion}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {submittingDiscussion ? 'Creating...' : 'Create Discussion'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Help Button */}
        <HelpButton pageName="community" />
      </div>
    </div>
  );
};

export default CommunityPage;
